import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminGetPost, adminCreatePost, adminUpdatePost, uploadMedia } from '../api/api';
import './AdminPostEditor.css';

const CATEGORIES = ['LOG', 'PHOTO', 'WORK', 'OBJECT', 'DIY', 'ZINES', 'SKETCH', 'MISC'];

const BLOCK_TYPES = [
  { type: 'paragraph', label: 'paragraf' },
  { type: 'heading', label: 'başlık' },
  { type: 'list', label: 'liste' },
  { type: 'blockquote', label: 'alıntı' },
  { type: 'image', label: 'görsel' },
  { type: 'audio', label: 'ses' },
  { type: 'video', label: 'video' },
  { type: 'table', label: 'tablo' },
  { type: 'code', label: 'kod' },
  { type: 'divider', label: 'ayırıcı' },
];

const createEmptyBlock = (type) => {
  switch (type) {
    case 'paragraph': return { type: 'paragraph', text: '' };
    case 'heading': return { type: 'heading', text: '', level: 2 };
    case 'list': return { type: 'list', style: 'bullet', items: [''] };
    case 'blockquote': return { type: 'blockquote', text: '' };
    case 'image': return { type: 'image', url: '', caption: '', metadataWarning: '', metadataWarningDismissed: false };
    case 'audio': return { type: 'audio', url: '', title: 'SES KAYDI', caption: '', metadataWarning: '', metadataWarningDismissed: false };
    case 'video': return { type: 'video', url: '', title: 'VİDEO', metadataWarning: '', metadataWarningDismissed: false };
    case 'table': return { type: 'table', headers: ['başlık 1', 'başlık 2'], rows: [['', '']] };
    case 'code': return { type: 'code', text: '' };
    case 'divider': return { type: 'divider' };
    default: return { type: 'paragraph', text: '' };
  }
};

const stripEditorOnlyFields = (blocks = []) =>
  (blocks || []).map(({ metadataWarning, metadataWarningDismissed, ...rest }) => rest);

const AdminPostEditor = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('LOG');
  const [publishedDate, setPublishedDate] = useState(new Date().toISOString().split('T')[0]);
  const [tagsInput, setTagsInput] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [blocks, setBlocks] = useState([createEmptyBlock('paragraph')]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [uploading, setUploading] = useState(false);
  const [coverUploadWarning, setCoverUploadWarning] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
    if (!authLoading && user && isEditing) {
      setLoading(true);
      adminGetPost(id)
        .then(post => {
          setTitle(post.title || '');
          setSlug(post.slug || '');
          setExcerpt(post.excerpt || '');
          setCategory(post.category || 'LOG');
          setPublishedDate(post.publishedDate || new Date().toISOString().split('T')[0]);
          setTagsInput(post.tags?.join(', ') || '');
          setCoverImageUrl(post.coverImageUrl || '');
          try {
            const parsed = JSON.parse(post.content || '[]');
            const cleaned = stripEditorOnlyFields(parsed);
            setBlocks(cleaned.length > 0 ? cleaned : [createEmptyBlock('paragraph')]);
          } catch {
            setBlocks([createEmptyBlock('paragraph')]);
          }
        })
        .catch(() => alert('Gönderi yüklenemedi.'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, user, authLoading, navigate]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Başlık gerekli.');
      return;
    }
    setSaving(true);
    try {
      const data = {
        title,
        slug: slug || undefined,
        excerpt,
        content: JSON.stringify(stripEditorOnlyFields(blocks)),
        category,
        publishedDate,
        coverImageUrl: coverImageUrl || undefined,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
      };

      if (isEditing) {
        await adminUpdatePost(id, data);
      } else {
        await adminCreatePost(data);
      }
      navigate('/admin');
    } catch (err) {
      alert('Kaydetme işlemi başarısız oldu.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, callback, onWarning) => {
    setUploading(true);
    try {
      const media = await uploadMedia(file);
      callback(media.url);
      if (onWarning) onWarning(media.warning || '');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.statusText || err?.message || 'unknown error';
      const status = err?.response?.status || '?';
      alert(`Dosya yüklenemedi. [${status}] ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(
        file,
        (url) => setCoverImageUrl(url),
        (warning) => setCoverUploadWarning(warning)
      );
    }
  };

  // ===== Block Operations =====

  const updateBlock = (index, updates) => {
    setBlocks(prev => prev.map((b, i) => i === index ? { ...b, ...updates } : b));
  };

  const addBlock = (type, afterIndex) => {
    const newBlock = createEmptyBlock(type);
    setBlocks(prev => {
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, newBlock);
      return updated;
    });
  };

  const removeBlock = (index) => {
    if (blocks.length <= 1) return;
    setBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    setBlocks(prev => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  // ===== Block Renderers =====

  const renderBlockEditor = (block, index) => {
    const blockHeader = (
      <div className="block-header">
        <span className="block-type-label">{BLOCK_TYPES.find(b => b.type === block.type)?.label || block.type}</span>
        <div className="block-controls">
          <button onClick={() => moveBlock(index, -1)} disabled={index === 0} title="Yukarı">↑</button>
          <button onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1} title="Aşağı">↓</button>
          <button onClick={() => removeBlock(index)} className="block-remove" title="Sil" disabled={blocks.length <= 1}>×</button>
        </div>
      </div>
    );

    switch (block.type) {
      case 'paragraph':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <textarea
              className="block-textarea"
              value={block.text}
              onChange={(e) => updateBlock(index, { text: e.target.value })}
              placeholder="Paragraf metni... (**kalın**, *italik* desteklenir)"
              rows={3}
            />
          </div>
        );

      case 'heading':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <div className="block-row">
              <select
                value={block.level}
                onChange={(e) => updateBlock(index, { level: parseInt(e.target.value) })}
                className="block-select-small"
              >
                <option value={2}>H2</option>
                <option value={3}>H3</option>
              </select>
              <input
                type="text"
                value={block.text}
                onChange={(e) => updateBlock(index, { text: e.target.value })}
                placeholder="Başlık metni"
                className="block-input"
              />
            </div>
          </div>
        );

      case 'list':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <select
              value={block.style}
              onChange={(e) => updateBlock(index, { style: e.target.value })}
              className="block-select-small"
              style={{ marginBottom: '8px' }}
            >
              <option value="bullet">madde işareti</option>
              <option value="numbered">numaralı</option>
            </select>
            {(block.items || []).map((item, i) => (
              <div key={i} className="block-list-item">
                <span className="list-item-bullet">{block.style === 'numbered' ? `${i + 1}.` : '•'}</span>
                <input
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const newItems = [...block.items];
                    newItems[i] = e.target.value;
                    updateBlock(index, { items: newItems });
                  }}
                  placeholder="Liste maddesi"
                  className="block-input"
                />
                <button
                  onClick={() => {
                    const newItems = block.items.filter((_, idx) => idx !== i);
                    updateBlock(index, { items: newItems.length ? newItems : [''] });
                  }}
                  className="list-item-remove"
                >×</button>
              </div>
            ))}
            <button
              onClick={() => updateBlock(index, { items: [...(block.items || []), ''] })}
              className="block-add-item"
            >+ madde ekle</button>
          </div>
        );

      case 'blockquote':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <textarea
              className="block-textarea blockquote-input"
              value={block.text}
              onChange={(e) => updateBlock(index, { text: e.target.value })}
              placeholder="Alıntı metni..."
              rows={2}
            />
          </div>
        );

      case 'image':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <div className="block-media-upload">
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(
                      file,
                      (url) => updateBlock(index, { url }),
                      (warning) => updateBlock(index, {
                        metadataWarning: warning || '',
                        metadataWarningDismissed: false,
                      })
                    );
                  }
                }}
              />
              {block.url && <img src={block.url.startsWith('http') ? block.url : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${block.url}`} alt="" className="block-preview-image" />}
            </div>
            {block.metadataWarning && !block.metadataWarningDismissed && (
              <div className="block-upload-warning">
                <span>{block.metadataWarning}</span>
                <button
                  type="button"
                  className="block-upload-warning-close"
                  onClick={() => updateBlock(index, { metadataWarningDismissed: true })}
                  aria-label="uyarıyı kapat"
                  title="Kapat"
                >
                  ×
                </button>
              </div>
            )}
            <input
              type="text"
              value={block.url}
              onChange={(e) => updateBlock(index, { url: e.target.value })}
              placeholder="Görsel URL"
              className="block-input"
            />
            <input
              type="text"
              value={block.caption || ''}
              onChange={(e) => updateBlock(index, { caption: e.target.value })}
              placeholder="Açıklama (opsiyonel)"
              className="block-input"
              style={{ marginTop: '6px' }}
            />
            <label style={{ display: 'block', marginTop: 8 }}>
              <input
                type="checkbox"
                checked={!!block.backgroundForNext}
                onChange={e => updateBlock(index, { backgroundForNext: e.target.checked })}
                style={{ marginRight: 6 }}
              />
              Sonraki metin bloğu için arka plan olarak kullan
            </label>
            {block.backgroundForNext && (
              <div style={{ marginTop: 8 }}>
                <span style={{ fontSize: 13, marginRight: 8 }}>Kırpma:</span>
                <label style={{ marginRight: 12 }}>
                  <input
                    type="radio"
                    name={`bgMode-${index}`}
                    value="cover"
                    checked={block.bgMode !== 'contain'}
                    onChange={() => updateBlock(index, { bgMode: 'cover' })}
                    style={{ marginRight: 4 }}
                  />
                  Fit (kırpabilir)
                </label>
                <label>
                  <input
                    type="radio"
                    name={`bgMode-${index}`}
                    value="contain"
                    checked={block.bgMode === 'contain'}
                    onChange={() => updateBlock(index, { bgMode: 'contain' })}
                    style={{ marginRight: 4 }}
                  />
                  Kırpma (tam göster)
                </label>
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <div className="block-media-upload">
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(
                      file,
                      (url) => updateBlock(index, { url }),
                      (warning) => updateBlock(index, {
                        metadataWarning: warning || '',
                        metadataWarningDismissed: false,
                      })
                    );
                  }
                }}
              />
            </div>
            {block.metadataWarning && !block.metadataWarningDismissed && (
              <div className="block-upload-warning">
                <span>{block.metadataWarning}</span>
                <button
                  type="button"
                  className="block-upload-warning-close"
                  onClick={() => updateBlock(index, { metadataWarningDismissed: true })}
                  aria-label="uyarıyı kapat"
                  title="Kapat"
                >
                  ×
                </button>
              </div>
            )}
            <input
              type="text"
              value={block.url}
              onChange={(e) => updateBlock(index, { url: e.target.value })}
              placeholder="Ses dosyası URL"
              className="block-input"
            />
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock(index, { title: e.target.value })}
              placeholder="Başlık (opsiyonel)"
              className="block-input"
              style={{ marginTop: '6px' }}
            />
            <input
              type="text"
              value={block.caption || ''}
              onChange={(e) => updateBlock(index, { caption: e.target.value })}
              placeholder="Açıklama (opsiyonel)"
              className="block-input"
              style={{ marginTop: '6px' }}
            />
          </div>
        );

      case 'video':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <div className="block-media-upload">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(
                      file,
                      (url) => updateBlock(index, { url }),
                      (warning) => updateBlock(index, {
                        metadataWarning: warning || '',
                        metadataWarningDismissed: false,
                      })
                    );
                  }
                }}
              />
            </div>
            {block.metadataWarning && !block.metadataWarningDismissed && (
              <div className="block-upload-warning">
                <span>{block.metadataWarning}</span>
                <button
                  type="button"
                  className="block-upload-warning-close"
                  onClick={() => updateBlock(index, { metadataWarningDismissed: true })}
                  aria-label="uyarıyı kapat"
                  title="Kapat"
                >
                  ×
                </button>
              </div>
            )}
            <input
              type="text"
              value={block.url}
              onChange={(e) => updateBlock(index, { url: e.target.value })}
              placeholder="Video dosyası URL"
              className="block-input"
            />
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock(index, { title: e.target.value })}
              placeholder="Başlık (opsiyonel)"
              className="block-input"
              style={{ marginTop: '6px' }}
            />
          </div>
        );

      case 'table':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <div className="block-table-editor">
              <div className="block-table-headers">
                {(block.headers || []).map((h, hi) => (
                  <input
                    key={hi}
                    type="text"
                    value={h}
                    onChange={(e) => {
                      const newHeaders = [...block.headers];
                      newHeaders[hi] = e.target.value;
                      updateBlock(index, { headers: newHeaders });
                    }}
                    placeholder={`Başlık ${hi + 1}`}
                    className="block-input table-cell-input"
                  />
                ))}
                <button
                  onClick={() => {
                    const newHeaders = [...(block.headers || []), ''];
                    const newRows = (block.rows || []).map(r => [...r, '']);
                    updateBlock(index, { headers: newHeaders, rows: newRows });
                  }}
                  className="table-add-col"
                >+sütun</button>
              </div>
              {(block.rows || []).map((row, ri) => (
                <div key={ri} className="block-table-row">
                  {row.map((cell, ci) => (
                    <input
                      key={ci}
                      type="text"
                      value={cell}
                      onChange={(e) => {
                        const newRows = block.rows.map((r, idx) =>
                          idx === ri ? r.map((c, cidx) => cidx === ci ? e.target.value : c) : r
                        );
                        updateBlock(index, { rows: newRows });
                      }}
                      className="block-input table-cell-input"
                    />
                  ))}
                  <button
                    onClick={() => {
                      const newRows = block.rows.filter((_, idx) => idx !== ri);
                      updateBlock(index, { rows: newRows });
                    }}
                    className="table-remove-row"
                  >×</button>
                </div>
              ))}
              <button
                onClick={() => {
                  const cols = block.headers?.length || 2;
                  const newRow = Array(cols).fill('');
                  updateBlock(index, { rows: [...(block.rows || []), newRow] });
                }}
                className="block-add-item"
              >+ satır ekle</button>
            </div>
          </div>
        );

      case 'code':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <textarea
              className="block-textarea code-input"
              value={block.text}
              onChange={(e) => updateBlock(index, { text: e.target.value })}
              placeholder="Kod bloğu..."
              rows={3}
            />
          </div>
        );

      case 'divider':
        return (
          <div key={index} className="block-editor">
            {blockHeader}
            <hr className="block-divider-preview" />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="editor-loading">yükleniyor...</div>;
  }

  return (
    <div className="editor-page">
      <div className="editor-header">
        <Link to="/admin" className="admin-back">← geri</Link>
        <h1>{isEditing ? 'gönderiyi düzenle' : 'yeni gönderi'}</h1>
        <button
          onClick={handleSave}
          className="editor-save-btn"
          disabled={saving || uploading}
        >
          {saving ? 'kaydediliyor...' : 'kaydet'}
        </button>
      </div>

      {uploading && <div className="upload-indicator">dosya yükleniyor...</div>}
      {coverUploadWarning && <div className="upload-warning">{coverUploadWarning}</div>}

      <div className="editor-form">
        {/* Post metadata */}
        <div className="editor-meta-section">
          <div className="form-group">
            <label>başlık *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Gönderi başlığı"
              className="editor-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>slug (opsiyonel)</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="otomatik oluşturulur"
                className="editor-input"
              />
            </div>

            <div className="form-group">
              <label>kategori *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="editor-select"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.toLowerCase()}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>tarih</label>
              <input
                type="date"
                value={publishedDate}
                onChange={(e) => setPublishedDate(e.target.value)}
                className="editor-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>özet</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Kart üzerinde görünecek kısa açıklama"
              className="editor-textarea"
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>etiketler (virgülle ayır)</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="etiket1, etiket2, etiket3"
              className="editor-input"
            />
          </div>

          <div className="form-group">
            <label>kapak görseli</label>
            <div className="cover-upload">
              <input type="file" accept="image/*,.heic,.heif" onChange={handleCoverUpload} />
              {coverImageUrl && (
                <div className="cover-preview">
                  <img src={coverImageUrl.startsWith('http') ? coverImageUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${coverImageUrl}`} alt="Cover" />
                  <button onClick={() => setCoverImageUrl('')} className="cover-remove">kaldır</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content blocks */}
        <div className="editor-content-section">
          <h2 className="section-title">içerik blokları</h2>

          {blocks.map((block, index) => (
            <React.Fragment key={index}>
              {renderBlockEditor(block, index)}

              {/* Add block button between blocks */}
              <div className="add-block-section">
                <div className="add-block-line" />
                <div className="add-block-buttons">
                  {BLOCK_TYPES.map(bt => (
                    <button
                      key={bt.type}
                      onClick={() => addBlock(bt.type, index)}
                      className="add-block-btn"
                      title={bt.label}
                    >
                      {bt.label}
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;
